import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import AdvanceTable from 'components/base/AdvanceTable';
import Badge from 'components/base/Badge';
import CustomModal from 'components/modals/CustomModal';
import CustomSalesReturnModal from 'components/modals/CustomSalesReturnModal';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useClearLocalStorageOnPathChange from 'hooks/useClearLocalStorageOnPathChange';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useBranchContext } from 'providers/BranchProvider';
import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Row, Table } from 'react-bootstrap';
import { Col } from 'react-bootstrap';
import { Card, Stack } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import BadgeStatus from 'utils/BadgeStatus';
import SalesInvoiceActions from 'utils/SalesInvoiceActions';
import { toastConfig } from 'utils/toastConfig';
import {
  TSalesInvoice,
  TSalesInvoiceItems,
  TSalesReturn,
  TUser
} from 'utils/types';
import * as Yup from 'yup';
import { useReactToPrint } from 'react-to-print';
import './styles.css';
import { formatDate } from 'utils/formats';

type TItemToReturn = {
  salesInvoiceId: number | null;
  productId: number | null;
  quantity: number;
  inspectedById: number | null;
  remarks: string;
  productName?: string;
};

type SalesReturnDetailsFormProps = {
  items: TItemToReturn[];
  staffList: TUser[];
  onChange: <K extends keyof TItemToReturn>(
    index: number,
    field: K,
    value: string | number
  ) => void;
};

type CancelReturnDetailsFormProps = {
  returnDetails: TSalesReturn;
  onChange: <K extends keyof TSalesReturn>(
    field: K,
    value: TSalesReturn[K]
  ) => void;
};

const SalesReturnDetailsForm = React.memo(
  ({ items, staffList, onChange }: SalesReturnDetailsFormProps) => {
    return (
      <>
        <Table responsive="sm">
          <thead>
            <tr>
              <td>Product Name</td>
              <td>Quantity</td>
              <td>Reason</td>
              <td>Inspected By</td>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td className="w-25">
                  <Form.Control
                    disabled
                    className="p-2"
                    value={item.productName || ''}
                  />
                </td>
                <td className="w-25">
                  <Form.Control
                    type="text"
                    className="p-2"
                    value={item.quantity}
                    onChange={e =>
                      onChange(index, 'quantity', Number(e.target.value) || 0)
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    type="text"
                    className="p-2"
                    value={item.remarks || ''}
                    onChange={e => onChange(index, 'remarks', e.target.value)}
                  />
                </td>
                <td>
                  <Form.Control
                    as="select"
                    className="p-2"
                    value={item.inspectedById || ''}
                    onChange={e =>
                      onChange(index, 'inspectedById', Number(e.target.value))
                    }
                  >
                    <option value="">Select Staff</option>
                    {staffList.map(staff => (
                      <option key={staff.userId} value={staff.userId}>
                        {staff.firstname} {staff.lastname}
                      </option>
                    ))}
                  </Form.Control>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  }
);

const CancelReturnDetailsForm = React.memo(
  ({ returnDetails, onChange }: CancelReturnDetailsFormProps) => {
    return (
      <>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="cancellationReason">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                type="text"
                value={returnDetails.cancellationReason || ''}
                onChange={e => onChange('cancellationReason', e.target.value)}
              />
            </Form.Group>
          </Row>
        </Form>
      </>
    );
  }
);

const getInitials = (name: string) => {
  const names = name.split(' ');
  const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
  return initials;
};

const SalesInvoiceDetails = () => {
  const { id } = useParams();
  const { selectedBranch } = useBranchContext();
  const [salesInvoiceDetails, setSalesInvoiceDetails] =
    useState<TSalesInvoice | null>(null);
  const [salesInvoiceItems, setSalesInvoiceItems] = useState<
    TSalesInvoiceItems[]
  >([]);
  const [selectedItems, setSelectedItems] = useState<TItemToReturn[]>([]);
  const returnModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const cancelReturnModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const [initiateReturn, setInitiateReturn] = useState<boolean>(false);
  const [salesStaff, setSalesStaff] = useState<TUser[]>([]);
  const [salesReturn, setSalesReturn] = useState<TSalesReturn[]>([]);
  const [returnDetails, setReturnDetails] = useState({
    cancellationReason: '',
    salesReturnId: 0
  });
  const [discountQrCode, setDiscountQrCode] = useState('');

  const contentRef = useRef<HTMLDivElement>(null);
  const getSalesInvoiceDetails = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${id}`
      );
      setSalesInvoiceDetails(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getSalesInvoiceItems = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${id}/invoiceItems`
      );
      setSalesInvoiceItems(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const salesReturnSchema = Yup.object().shape({
    items: Yup.array()
      .of(
        Yup.object().shape({
          quantity: Yup.number()
            .transform((value, originalValue) =>
              originalValue === '' ? undefined : Number(originalValue)
            )
            .required('Quantity is required')
            .positive('Quantity must be positive')
            .integer('Quantity must be an integer'),
          remarks: Yup.string().required('Remarks are required'),
          inspectedById: Yup.number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null
                ? undefined
                : Number(originalValue)
            )
            .required('Inspected By is required')
            .min(1, 'Inspected By is required')
        })
      )
      .min(1, 'At least one item must be returned')
  });

  const cancelReturnSchema = Yup.object().shape({
    cancellationReason: Yup.string().required('Cancellation reason is required')
  });

  const getSalesStaff = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/users?role=1`
      );
      setSalesStaff(res.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  const getSalesReturn = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${id}/returns`
      );
      setSalesReturn(res.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data?.errorMessage);
      }
    }
  };

  const handleCheckboxChange = (item: TSalesInvoiceItems) => {
    const salesInvoiceId = id !== undefined ? parseInt(id) : 0;
    const isSelected = selectedItems.some(
      selected =>
        (item.productId !== null && selected.productId === item.productId) ||
        (item.productId === null && selected.productName === item.productName)
    );
    if (isSelected) {
      setSelectedItems(prev =>
        prev.filter(
          selected =>
            (item.productId !== null &&
              selected.productId !== item.productId) ||
            (item.productId === null &&
              selected.productName !== item.productName)
        )
      );
    } else {
      setSelectedItems(prev => [
        ...prev,
        {
          salesInvoiceId: salesInvoiceId,
          productId: item.productId,
          quantity: item.quantity,
          inspectedById: null,
          remarks: '',
          productName: item?.productName || ''
        }
      ]);
    }
  };

  const handleSalesReturnFormChange = (
    index: number,
    field: keyof TItemToReturn,
    value: string | number
  ) => {
    setSelectedItems(prevData => {
      const updatedData = [...prevData];
      updatedData[index] = { ...updatedData[index], [field]: value };
      return updatedData;
    });
  };

  const handleOpenReturnModal = () => {
    returnModalRef.current.show();
  };

  const handleInitiateReturn = () => {
    setInitiateReturn(true);
  };

  const handleCancelInitiateReturn = () => {
    setInitiateReturn(false);
    setSelectedItems([]);
  };

  const handleSubmitSelectedItems = async () => {
    try {
      await salesReturnSchema.validate(
        { items: selectedItems },
        { abortEarly: false }
      );

      const formattedItems = selectedItems.map(item => ({
        salesInvoiceId: item.salesInvoiceId,
        productId: item.productId || null,
        noIdProductName: item.productId ? null : item.productName,
        quantity: Number(item.quantity),
        inspectedById: item.inspectedById,
        remarks: item.remarks
      }));

      for (const item of formattedItems) {
        await axiosInstance.post(
          `/api/v1/${selectedBranch?.branchDomain}/salesReturns`,
          item
        );
      }
      toast.success('Sales return submitted successfully!', {
        ...toastConfig,
        autoClose: 2000
      });
      setInitiateReturn(false);
      setSelectedItems([]);
      returnModalRef.current.hide();
      getSalesInvoiceDetails();
      getSalesReturn();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Unable to submit the sales return.',
          {
            ...toastConfig,
            autoClose: 3000
          }
        );
      }
    }
  };

  const handleCancel = (returnItem: TSalesReturn) => {
    setReturnDetails({
      cancellationReason: returnItem.cancellationReason ?? '',
      salesReturnId: returnItem.salesReturnId ?? 0
    });
    cancelReturnModalRef.current.show();
  };

  const handleCancelFormChange = <K extends keyof TSalesReturn>(
    field: K,
    value: TSalesReturn[K]
  ) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitCancelReturn = async () => {
    try {
      await cancelReturnSchema.validate(returnDetails);

      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesReturns/${returnDetails.salesReturnId}/cancel`,
        {
          cancellationReason: returnDetails.cancellationReason
        }
      );
      cancelReturnModalRef.current.hide();
      toast.success('Return Cancelled', {
        ...toastConfig,
        autoClose: 2000
      });
      getSalesReturn();
      getSalesInvoiceDetails();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        toast.error(error.message, { ...toastConfig, autoClose: 3000 });
      } else if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Unable to cancel the sales return.',
          {
            ...toastConfig,
            autoClose: 3000
          }
        );
      }
    }
  };

  const handleSubmitQrCode = async () => {
    try {
      const res = await axiosInstance.post(
        `/api/qrcode?text=${salesInvoiceDetails?.afterSalesDiscountCode}`,
        {},
        {
          responseType: 'blob'
        }
      );
      const qrCodeUrl = URL.createObjectURL(res.data);
      setDiscountQrCode(qrCodeUrl);
      // console.log(res.data)
    } catch (error) {
      console.error(error);
    }
  };

  // TODO: refactor
  const reactToPrintFn = useReactToPrint({ content: () => contentRef.current });

  useEffect(() => {
    getSalesInvoiceDetails();
    getSalesInvoiceItems();
    getSalesStaff();
    getSalesReturn();
  }, [id, selectedBranch?.branchDomain]);

  useEffect(() => {
    handleSubmitQrCode();
    return () => {
      if (discountQrCode) {
        URL.revokeObjectURL(discountQrCode);
      }
    };
  }, [salesInvoiceDetails?.uniqueCode]);

  useClearLocalStorageOnPathChange();

  const columns: ColumnDef<TSalesInvoiceItems>[] = [
    ...(initiateReturn
      ? [
          {
            header: 'Select',
            cell: ({ row }: { row: { original: TSalesInvoiceItems } }) => {
              const item = row.original;
              const isSelected = selectedItems.some(
                selected =>
                  (item.productId !== null &&
                    selected.productId === item.productId) ||
                  (item.productId === null &&
                    selected.productName === item.productName)
              );

              return (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleCheckboxChange(item)}
                />
              );
            },
            enableSorting: false
          }
        ]
      : []),
    {
      header: 'Product Name',
      accessorKey: 'productName',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link
            to={`/product/${encodeURIComponent(
              selectedBranch?.branchDomain as string
            )}/${original.productId}`}
            className="text-decoration-none fw-bold fs-9"
          >
            {original.productName}
          </Link>
        );
      }
    },
    {
      header: 'Quantity',
      accessorKey: 'quantity',
      enableSorting: false
    },
    {
      header: 'Unit Price',
      accessorKey: 'unitPrice',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { unitPrice, discountedUnitPrice, discountApplied } = original;
        return (
          <span>
            <span className="me-2">
              {discountedUnitPrice ? discountedUnitPrice : unitPrice}
            </span>
            {discountApplied && (
              <>
                <Badge
                  bg="danger"
                  variant="phoenix"
                  className="fs-10 pe-none me-2"
                >
                  save {discountApplied}
                </Badge>
                <del>{unitPrice}</del>
              </>
            )}
          </span>
        );
      }
    },
    {
      header: 'Amount',
      accessorKey: 'amount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { discountApplied, discountedAmount, amount } = original;
        return (
          <span>
            <span className="me-2">
              {discountedAmount ? discountedAmount : amount}
            </span>
            {discountApplied && (
              <span>
                <del>{amount}</del>
              </span>
            )}
          </span>
        );
      }
    },
    {
      header: 'Unit of Measure',
      accessorKey: 'unitOfMeasure',
      enableSorting: false
    }
  ];

  const returnColumns: ColumnDef<TSalesReturn>[] = [
    {
      header: 'Product Name',
      accessorKey: 'productName',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link
            to={`/product/${encodeURIComponent(
              selectedBranch?.branchDomain as string
            )}/${original.productId}`}
            className="text-decoration-none fw-bold fs-9"
          >
            {original.productName}
          </Link>
        );
      }
    },
    {
      header: 'Quantity',
      accessorKey: 'quantityReturned',
      enableSorting: false
    },
    {
      header: 'Cancellation Reason',
      accessorKey: 'cancellationReason',
      enableSorting: false
    },
    {
      header: 'Cancel Return',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (original.isCancelled) {
          return (
            <Badge bg="secondary" variant="phoenix" className="fs-10">
              Cancelled
            </Badge>
          );
        }

        return (
          <FontAwesomeIcon
            icon={faEdit}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              handleCancel(original);
            }}
          />
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: salesInvoiceItems,
    columns,
    pageSize: 20,
    pagination: true,
    sortable: true
  });

  const returnsTable = useAdvanceTable({
    data: salesReturn,
    columns: returnColumns,
    pageSize: 20,
    pagination: true,
    sortable: true
  });

  return (
    <>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-baseline mb-4"
      >
        <Row lg="auto">
          {salesInvoiceDetails?.status !== 'Submitted' && (
            <>
              <SalesInvoiceActions
                salesInvoiceDetails={salesInvoiceDetails}
                branchDomain={selectedBranch?.branchDomain}
                refetch={getSalesInvoiceDetails}
                salesInvoiceId={id}
              />

              <div className="d-flex align-items-baseline w-50">
                <h4>Sales Details</h4>
              </div>
            </>
          )}
        </Row>
      </Stack>

      <Card style={{ marginBottom: '20px' }}>
        <div className="d-flex">
          <Card.Body>
            <Col>
              <Card.Title as="h5">
                <span>Sales Id: {salesInvoiceDetails?.salesInvoiceId}</span>
                <span className="ms-1">
                  <BadgeStatus status={salesInvoiceDetails?.status} />
                </span>
                <span className="ms-1">
                  {salesInvoiceDetails?.isForDeletion ? (
                    <Badge bg="danger" variant="phoenix" className="fs-10">
                      Delete Requested
                    </Badge>
                  ) : null}
                </span>
              </Card.Title>
              <Card.Title as="h6">
                Invoice Number: {salesInvoiceDetails?.invoiceNumber}
              </Card.Title>
              <Card.Title as="h6">
                Remarks: {salesInvoiceDetails?.remarks}
              </Card.Title>
              <Card.Title as="h6">
                Unique Code: {salesInvoiceDetails?.uniqueCode}
              </Card.Title>
              {salesInvoiceDetails?.discountCodeUsed && (
                <Card.Title as="h6">
                  Discount code used: {salesInvoiceDetails?.discountCodeUsed}
                </Card.Title>
              )}
            </Col>
            <Col>
              <Button style={{ width: '10rem' }} onClick={reactToPrintFn}>
                Print Receipt
              </Button>
            </Col>
          </Card.Body>
        </div>
      </Card>
      <div className="d-flex justify-content-end mb-4">
        {initiateReturn && (
          <Button
            variant="danger"
            style={{ width: '6rem', marginRight: '0.4rem' }}
            onClick={handleCancelInitiateReturn}
          >
            Cancel
          </Button>
        )}
        {salesInvoiceDetails?.status == 'Submitted' && !initiateReturn && (
          <Button style={{ width: '10rem' }} onClick={handleInitiateReturn}>
            Initiate Return
          </Button>
        )}
      </div>
      <div className="mb-6">
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              size: 'lg',
              className:
                'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
            }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
            bodyClassName="py-3"
          />
        </AdvanceTableProvider>
      </div>
      <h4>
        Sub Total:{' '}
        {salesInvoiceDetails?.discountedTotal
          ? salesInvoiceDetails?.discountedTotal
          : salesInvoiceDetails?.subtotal}
      </h4>

      {initiateReturn && (
        <Button
          onClick={handleOpenReturnModal}
          disabled={selectedItems.length == 0}
          className="mb-4"
        >
          Return
        </Button>
      )}
      {(salesInvoiceDetails?.hasReturns == true || salesReturn.length > 0) && (
        <div className="mb-6 mt-4">
          <h5 className="mb-3">For Returns</h5>
          <AdvanceTableProvider {...returnsTable}>
            <AdvanceTable
              tableProps={{
                size: 'lg',
                className:
                  'phoenix-table fs-9 mb-0 p-5 border-top border-translucent'
              }}
              rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
              bodyClassName="py-3"
            />
          </AdvanceTableProvider>
        </div>
      )}
      <h4>Returns Total: {salesInvoiceDetails?.returnAmount}</h4>

      <h2 className="border-top border-solid mt-4 pt-2">
        Total:{' '}
        {salesInvoiceDetails?.discountedTotal
          ? salesInvoiceDetails?.discountedTotal
          : salesInvoiceDetails?.total}
      </h2>
      <CustomSalesReturnModal
        ref={returnModalRef}
        title="Sales Return Details"
        content={
          <SalesReturnDetailsForm
            items={selectedItems}
            staffList={salesStaff}
            onChange={handleSalesReturnFormChange}
          />
        }
        onSubmit={handleSubmitSelectedItems}
      />
      <CustomModal
        ref={cancelReturnModalRef}
        title="Cancel Sales Return"
        content={
          <CancelReturnDetailsForm
            returnDetails={returnDetails}
            onChange={handleCancelFormChange}
          />
        }
        onSubmit={handleSubmitCancelReturn}
      />
      <div ref={contentRef}>
        <div
          style={{
            padding: '20px',
            border: '1px solid black',
            visibility: 'hidden'
          }}
          id="printable"
        >
          <div className="receipt-main-content">
            <div className="receipt-content">
              <h4>Order Summary</h4>
              <div>Order Summary no.: {salesInvoiceDetails?.invoiceNumber}</div>
              <div>Customer Name: {salesInvoiceDetails?.soldTo}</div>
              <div>
                Date:{' '}
                {salesInvoiceDetails?.invoicedOn &&
                  formatDate(salesInvoiceDetails?.invoicedOn)}
              </div>
              <div>
                Prepared by:{' '}
                {salesInvoiceDetails?.createdBy &&
                  getInitials(salesInvoiceDetails.createdBy)}
              </div>
              <div>Payment: {salesInvoiceDetails?.paymentMethodName}</div>
              <div
                style={{
                  padding: '8px 0'
                }}
              >
                {salesInvoiceItems?.map((item: TSalesInvoiceItems) => (
                  <Row
                    key={item.salesInvoiceItemId || item.productId}
                    className="align-items-center py-2"
                    style={{
                      borderBottom: '1px solid #ccc',
                      display: 'flex'
                    }}
                  >
                    <Col style={{ flex: 2, textAlign: 'left' }}>
                      {item.productName}
                    </Col>
                    <Col style={{ flex: 1, textAlign: 'center' }}>
                      {item.quantity}
                    </Col>
                    <Col style={{ flex: 1, textAlign: 'center' }}>
                      {item.discountedUnitPrice
                        ? item.discountedUnitPrice
                        : item.unitPrice}
                    </Col>
                    <Col
                      style={{ flex: 1, textAlign: 'right', fontWeight: 'bold' }}
                    >
                      {item.discountedAmount ? item.discountedAmount : item.amount}
                    </Col>
                  </Row>
                ))}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  minWidth: '100px',
                  borderBottom: '1px solid #ddd',
                  padding: '8px 0'
                }}
              >
                Subtotal:{' '}
                {salesInvoiceDetails?.discountedTotal
                  ? salesInvoiceDetails?.discountedTotal
                  : salesInvoiceDetails?.subtotal}
              </div>
              <div
                style={{
                  textAlign: 'right',
                  minWidth: '100px',
                  borderBottom: '1px solid #ddd',
                  padding: '8px 0'
                }}
              >
                Total:{' '}
                <strong>
                  {salesInvoiceDetails?.discountedTotal
                    ? salesInvoiceDetails?.discountedTotal
                    : salesInvoiceDetails?.total}
                </strong>
              </div>
              <p>Remarks: {salesInvoiceDetails?.remarks}</p>
            </div>
            
            {/* Discount Voucher Section */}
            {salesInvoiceDetails?.afterSalesDiscountCode && salesInvoiceDetails?.afterSalesDiscountCode.trim() !== '' && (
              <div className="discount-voucher">
                <h5 style={{ marginBottom: '10px', fontSize: '14px', textAlign: 'center' }}>
                  Discount Voucher
                </h5>
                <div style={{ textAlign: 'center' }}>
                  <img 
                    src={discountQrCode} 
                    style={{ 
                      width: '70mm', 
                      height: '70mm', 
                      marginBottom: '10px',
                      border: '1px solid #ddd'
                    }} 
                    alt="Discount QR Code"
                  />
                </div>
                <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                  <p style={{ margin: '5px 0', textAlign: 'center' }}>
                    <strong>Code: {salesInvoiceDetails?.afterSalesDiscountCode}</strong>
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    Be a MEMBER to use this discount code for your next purchase.
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    {salesInvoiceDetails?.afterSalesDiscountDescription}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    Active from: {salesInvoiceDetails?.afterSalesDiscountActiveDate ? formatDate(salesInvoiceDetails.afterSalesDiscountActiveDate.toString()) : 'N/A'}
                  </p>
                  <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
                    Limited to one use.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesInvoiceDetails;
